import { axiosInstance as api } from "../helpers/request";
import {
  ICreatePlaylistRequest,
  ICreateVideoRequest,
  IUpdateLiveStreamRequest,
  IUpdatePlaylistRequest,
  IUpdateVideoRequest,
} from "../types/interfaces";
import {
  ILiveStream,
  ILiveStreamAnalytics,
  IMediaApply,
  IPlaylist,
  IVideo,
} from "../types/models";

class LiveStreamService {
  getActiveLiveStreams() {
    return api.get<ILiveStream[]>("/live-stream/active");
  }

  getLiveStreamById(id: string) {
    return api.get<ILiveStream>(`/live-stream/${id}`);
  }

  // Streams are provisioned by checkout / trial activation; there is no
  // create route. Starting is explicit (entitlement-gated on the api).
  startLiveStream(id: string) {
    return api.post<{ status: string; streamId: string; mediaApply?: IMediaApply | null }>(
      `/live-stream/${id}/start`,
    );
  }

  createVideo(streamId: string, data: ICreateVideoRequest) {
    return api.post<IVideo>(`/streams/${streamId}/videos`, data);
  }

  updateVideo(streamId: string, data: IUpdateVideoRequest) {
    return api.put<IVideo>(`/streams/${streamId}/videos/${data.id}`, data);
  }

  batchCreateVideo(streamId: string, videos: ICreateVideoRequest[]) {
    return api.post<IVideo[]>(`/streams/${streamId}/videos/batch`, { videos });
  }

  batchUpdateVideos(streamId: string, videos: IUpdateVideoRequest[]) {
    return api.patch<IVideo[]>(`/streams/${streamId}/videos/batch`, { videos });
  }

  createPlaylist(streamId: string, data: ICreatePlaylistRequest) {
    return api.post<IPlaylist>(`/streams/${streamId}/playlists`, data);
  }

  updatePlaylist(streamId: string, data: IUpdatePlaylistRequest) {
    return api.put<IPlaylist>(`/streams/${streamId}/playlists/${data.id}`, data);
  }

  batchCreatePlaylist(streamId: string, playlists: ICreatePlaylistRequest[]) {
    return api.post<IPlaylist[]>(`/streams/${streamId}/playlists/batch`, { playlists });
  }

  batchUpdatePlaylists(streamId: string, playlists: IUpdatePlaylistRequest[]) {
    return api.patch<IPlaylist[]>(`/streams/${streamId}/playlists/batch`, { playlists });
  }

  updatePromoted(data: { id: string; promoted: boolean; promotedUrl: string }) {
    return api.put<ILiveStream>(`/live-stream/${data.id}/promote`, data);
  }

  /** platformId is the destination's own id (StreamingPlatform.id). */
  updateVideoKey(data: { id: string; platformId: string; videoKey: string }) {
    return api.put<boolean>(`/live-stream/${data.id}/videoKey`, data);
  }

  updateLiveStream(data: IUpdateLiveStreamRequest) {
    // B3: `refresh: "deferred"` = saved, but the running worker was not updated yet.
    return api.put<ILiveStream & { refresh?: "deferred" }>(
      `/live-stream/${data.id}`,
      data,
    );
  }

  /** B4: runs every gate of the settings PUT without writing. Call it before any content write. */
  validateLiveStreamUpdate(data: IUpdateLiveStreamRequest) {
    return api.put<{ valid: true }>(
      `/live-stream/${data.id}?validateOnly=true`,
      data
    );
  }

  restartLiveStream(id: string) {
    return api.put<ILiveStream>(`/live-stream/${id}/restart`);
  }

  /** itemId is a media id (VisualMedia.id or Audio.id). Pro/4K only. */
  playLiveStreamItem(id: string, itemId: string) {
    return api.put<ILiveStream>(`/live-stream/${id}/play_item/${itemId}`);
  }

  endLiveStream(id: string) {
    return api.put<ILiveStream>(`/live-stream/${id}/end`);
  }

  stopLiveStream(id: string) {
    return api.put<ILiveStream>(`/live-stream/${id}/stop`);
  }

  /** id is a destination id (StreamingPlatform.id). */
  getLiveStreamAnalyticsById(id: string, startDate: string, endDate: string) {
    return api.get<ILiveStreamAnalytics[]>(
      `/stream-analytics/${id}/analytics?startDate=${startDate}&endDate=${endDate}`
    );
  }
}

const LiveStreamServiceInstance = new LiveStreamService();

export default LiveStreamServiceInstance;
